import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Save, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCoverageAreas = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [areas, setAreas] = useState([]);
  const [newArea, setNewArea] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAreas();
  }, [navigate]);

  const fetchAreas = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/coverage-areas`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAreas(data);
      }
    } catch (error) {
      toast.error('Failed to load coverage areas');
    } finally {
      setLoading(false);
    }
  };

  const handleAddArea = () => {
    if (newArea.trim()) {
      setAreas([...areas, newArea.trim()]);
      setNewArea('');
    }
  };

  const handleRemoveArea = (index) => {
    const updated = [...areas];
    updated.splice(index, 1);
    setAreas(updated);
  };

  const handleUpdateArea = (index, value) => {
    const updated = [...areas];
    updated[index] = value;
    setAreas(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      await fetch(`${BACKEND_URL}/api/admin/coverage-areas`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(areas.filter(area => area.trim()))
      });
      
      toast.success('Coverage areas updated successfully!');
      fetchAreas();
    } catch (error) {
      toast.error('Failed to update coverage areas');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="icon" className="text-white border-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Coverage Areas Management</h1>
              <p className="text-cyan-100">Update service coverage areas list</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-cyan-600 hover:bg-cyan-50">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Service Coverage Areas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Add new area */}
            <div className="flex gap-2">
              <Input
                value={newArea}
                onChange={(e) => setNewArea(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddArea()}
                placeholder="Enter area name (e.g., Neath, Swansea)"
                className="flex-1"
              />
              <Button onClick={handleAddArea} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Area
              </Button>
            </div>

            {/* Areas list */}
            <div className="border rounded-lg divide-y">
              {areas.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No coverage areas yet. Add your first area above.
                </div>
              ) : (
                areas.map((area, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 hover:bg-gray-50">
                    <span className="text-gray-500 w-8">{index + 1}.</span>
                    <Input
                      value={area}
                      onChange={(e) => handleUpdateArea(index, e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRemoveArea(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> These areas will be displayed on the "Areas We Cover" page. 
                Make sure to list all towns and neighborhoods within the 15-mile radius from Neath.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCoverageAreas;
